/**
 * Why a calculation step gave no score, in a clinician's words: the same codes and wording as pm-react's run view
 * (`src/studio/lib/calc-not-computed-reason.ts`, PR #1339).
 *
 * Scoring rule 1 (Samer, 22 Oct 2026): no score unless it is valid, and the clinician sees "Not calculated: <reason>".
 * Refusing to score is the correct clinical result (PREVENT for a patient with known CVD), never a system error. The
 * engine (Scrips.Workflows #192) writes the reason as a CODE to `calc.<engineId>.reason`. `configuration` is the one
 * code that is a real fault: the step is not set up to run. With no reason on the run (an engine without #192, or an
 * older run) the line is "Not calculated — see inputs". Pure.
 */

/** The run variable holding why a calculation gave no score, e.g. `calc.C07-prevent.reason`. */
export const calcReasonVariable = (engineId: string): string => `calc.${engineId}.reason`;

/** The engine's code for a step that is not set up to run: a fault, not a clinical result. */
export const CONFIGURATION_REASON = 'configuration';

/** The line after "Not calculated: " for every code CalculationReasonCodes.cs defines, except `configuration`. */
export const CALC_REASON_DETAIL: Readonly<Record<string, string>> = {
  'known-cvd': 'known cardiovascular disease — PREVENT applies to primary prevention only',
  'cvd-status-unknown': 'cardiovascular disease status not recorded — PREVENT needs a confirmed "no known CVD"',
  'age-out-of-range': "the patient's age is outside the range this score is validated for",
  'input-out-of-range': 'an input is outside the range this score is validated for',
  'missing-input': 'a required input is missing',
  'wrong-unit': 'an input was recorded in a unit this score cannot use',
  'invalid-input': 'an input is not a valid value',
  'review-required': 'a clinician must confirm this score applies before it is shown',
  'not-applicable': "this score does not apply to the patient's current situation",
};

/** Shown when the run holds no reason (or a code this map does not know): never a raw code. */
export const NOT_CALCULATED_SEE_INPUTS = 'Not calculated — see inputs';
/** Shown for a real fault: the step could not run as set up. */
export const CALC_FAULT = 'Calculation failed: the step is not set up to run';

const codeOf = (reason: unknown): string | null =>
  typeof reason === 'string' && reason.trim() ? reason.trim().toLowerCase() : null;

/** True when the reason is a real fault (the step is not set up to run). */
export function isCalcFault(reason: unknown): boolean {
  return codeOf(reason) === CONFIGURATION_REASON;
}

/** "Not calculated: <reason>" for a known code; "Not calculated — see inputs" otherwise. */
export function notCalculatedText(reason: unknown): string {
  const code = codeOf(reason);
  const detail = code ? CALC_REASON_DETAIL[code] : undefined;
  return detail ? `Not calculated: ${detail}` : NOT_CALCULATED_SEE_INPUTS;
}
